<template>
	<div class="font-sansCondensed flex p-5 pt-5 print:p-0">
		<div class="w-full print:w-full">
			<div class="mb-4 flex w-full justify-between">
				<div class="flex items-center">
					<PlanAvatar
						class="font-sans"
						:plan="plan"
						:index="index"
						large
						filled
					/>
					<div class="text-secondary-900 ml-3 font-serif text-2xl">
						{{ plan.firstName }} {{ plan.lastName }}
					</div>
				</div>
				<div class="flex items-center">
					<Cog8ToothIcon
						class="text-secondary-300 h-6 w-6 cursor-pointer print:hidden"
						@click="emit('openCustomItemModal')"
					/>
				</div>
			</div>

			<div
				class="flex gap-5"
				:class="{ 'flex-col': !isSinglePlanSession }"
			>
				<!-- Funeral Services -->
				<div class="flex-1 rounded-lg border border-slate-200 p-4 shadow-md">
					<div class="flex items-center">
						<h3 class="font-sansCondensed text-lg font-bold text-slate-900">
							Funeral Services
						</h3>
						<ShieldCheckIcon class="text-accent-600 ml-2 h-5 w-5" />
					</div>

					<!-- Package Item -->
					<CartItem
						v-if="packageItem"
						:cart-item="packageItem"
						:plan="plan"
						isPackage
						@delete="plan.delete(packageItem)"
					/>

					<!-- Cart Packages - Non-Essentials -->
					<CartItem
						class="pt-3"
						v-for="cartItem in serviceCartItems"
						:key="getReviewSlideCartEntryKey(cartItem)"
						:cart-item="getCartEntryForDisplay(cartItem)"
						:plan="plan"
						@delete="plan.delete(cartItem)"
					/>

					<!-- Custom Cart Items for the Service Section -->
					<CartCustomItem
						v-for="customItem in serviceCustomCartItems"
						:key="customItem.id"
						:item="customItem"
						:plan="plan"
						@delete="plan.hardDeleteCustomItem(customItem.id)"
					/>

					<!-- OAP (for non-essential packages only) -->
					<CartOapItem
						:plan="plan"
						:parent-deleted="oapParentDeleted(plan)"
						@update:oap-included="handleOapChange"
					/>
				</div>

				<!-- Funeral Merchandise -->
				<div
					v-if="
						merchandiseCartItems.length > 0 ||
						merchandiseCustomCartItems.length > 0
					"
					class="flex-1 rounded-lg border border-slate-200 p-4 shadow-md"
				>
					<div class="flex items-center">
						<h3 class="text-secondary-900 font-sansCondensed text-lg font-bold">
							Funeral Merchandise
						</h3>
						<ShieldCheckIcon class="text-accent-600 ml-2 h-5 w-5" />
					</div>
					<!-- Cart Items -->
					<CartItem
						class="pt-3"
						v-for="cartItem in merchandiseCartItems"
						:key="getReviewSlideCartEntryKey(cartItem)"
						:cart-item="getCartEntryForDisplay(cartItem)"
						:plan="plan"
						@delete="plan.delete(cartItem)"
					/>

					<!-- Custom Cart Items for the Merchandise Section -->
					<CartCustomItem
						v-for="customItem in merchandiseCustomCartItems"
						:key="customItem.id"
						:item="customItem"
						:plan="plan"
						@delete="plan.hardDeleteCustomItem(customItem.id)"
					/>
				</div>

				<!-- Essentials -->
				<div
					v-if="
						cashAdvanceCartItems.length > 0 ||
						essentialsCustomCartItems.length > 0 ||
						showEverplansCartLine
					"
					class="flex-1 rounded-lg border border-slate-200 p-4 shadow-md"
				>
					<h3 class="text-secondary-900 font-sansCondensed text-lg font-bold">
						Essentials
					</h3>

					<!-- Cart Packages - Essentials -->
					<CartItem
						class="pt-3"
						v-for="cartItem in cashAdvanceCartItems"
						:key="getReviewSlideCartEntryKey(cartItem)"
						:cart-item="getCartEntryForDisplay(cartItem)"
						:plan="plan"
						@delete="plan.delete(cartItem)"
					/>

					<!-- Custom Cart Items for the Essentials Section -->
					<CartCustomItem
						v-for="customItem in essentialsCustomCartItems"
						:key="customItem.id"
						:item="customItem"
						:plan="plan"
						@delete="plan.hardDeleteCustomItem(customItem.id)"
					/>

					<!-- Everplans -->
					<CartEverplansItem
						v-if="showEverplansCartLine"
						:class="{ 'print:hidden': !plan.everplansIncluded }"
						:plan="plan"
						@update:everplans-included="handleEverplansChange"
					/>
				</div>
			</div>

			<!-- footer -->
			<div class="flex flex-col items-end pt-6">
				<div class="grid grid-cols-2 gap-6">
					<div class="flex flex-col justify-end gap-2">
						<span class="flex items-center justify-end gap-1">
							<span>Total Guaranteed</span>
							<ShieldCheckIcon class="text-accent-600 -mt-1 h-3.5 w-3.5" />
						</span>
						<span class="text-right">Total Non-Guaranteed</span>
						<div
							v-if="planPrice"
							class="flex-1 pt-4 pb-2 text-right font-sans text-xl font-bold text-slate-900"
						>
							{{ t('reviewSlide.total') }}
						</div>
					</div>
					<div class="flex flex-col gap-2 text-right">
						<span>{{ formatPrice(planPrice.guaranteedTotal) }}</span>
						<span>{{ formatPrice(planPrice.nonGuaranteedTotal) }}</span>
						<div
							v-if="planPrice"
							class="pt-4 pb-2 font-sans text-xl font-bold text-slate-900"
						>
							{{ formatPrice(planPrice.total) }}
						</div>
					</div>
				</div>
			</div>
			<div>
				<BaseAlert
					v-if="otherLine2Conflict"
					type="warning"
					class="mb-4 justify-self-end"
				>
					{{ t('reviewSlide.transportationOther2PackageConflict') }}
				</BaseAlert>
				<button
					type="button"
					@click="onAdvanceButtonClick"
					:disabled="otherLine2Conflict"
					data-test-id="advance-button"
					:class="{ 'w-full': !isSinglePlanSession }"
					class="bg-accent-400 text-accent-700 hover:bg-accent-700 disabled:bg-secondary-300 disabled:hover:bg-secondary-300 flex items-center justify-center gap-2 justify-self-end rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white disabled:cursor-not-allowed disabled:text-slate-500 disabled:hover:text-slate-500 print:hidden"
				>
					{{ t('reviewSlide.seePaymentOptions') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import BaseAlert from '../common/BaseAlert.vue'
import CartCustomItem from '../merchandise/CartCustomItem.vue'
import CartEverplansItem from '../merchandise/CartEverplansItem.vue'
import CartItem from '../merchandise/CartItem.vue'
import CartOapItem from '../merchandise/CartOapItem.vue'
import PlanAvatar from '../presentation/PlanAvatar.vue'
import {
	getReviewSlideCartEntryKey,
	getReviewSlideCartItemsBySection,
	getReviewSlideCustomCartItemsBySection,
} from './reviewSlideCartItems'
import type { ReviewSlideCartEntry } from './reviewSlideCartItems'
import Plan, { PlanErrorType } from '@/classes/Plan'
import { DocumentType } from '@/enums/DocumentType'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { isCartSubItem } from '@/guards'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type {
	CartItemAndLineItem,
	CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useDocumentStore } from '@/stores/document'
import { usePlanStore } from '@/stores/plan'
import { usePresentationController } from '@/stores/presentationController'
import type { CustomCartItem } from '@/types'
import { ShieldCheckIcon, Cog8ToothIcon } from '@heroicons/vue/24/solid'
import { computed } from 'vue'

const emit = defineEmits<{
	(e: 'openCustomItemModal'): void
}>()

const props = defineProps<{
	plan: Plan
	index: number
	otherLine2Conflict: boolean
	isSinglePlanSession: boolean
}>()

const authStore = useAuthStore()
const documentStore = useDocumentStore()
const planStore = usePlanStore()
const cartStore = useCartStore()
const { t } = usePresentationLocale()

const { calculateCartTotals, getPlanCartItems, formatPrice } = useMerchandise()

const planPrice = computed(() => {
	const totals = calculateCartTotals(props.plan)
	const guaranteedTotal =
		totals.guaranteedFuneralSubtotal +
		(props.plan.oapIncluded ? totals.oapSubTotal : 0)
	const nonGuaranteedTotal =
		totals.nonGuaranteedFuneralSubTotal +
		(props.plan.everplansIncluded ? totals.everplansSubTotal : 0)

	return { total: totals.grandTotal, guaranteedTotal, nonGuaranteedTotal }
})

const packageItem = computed<CartItemAndLineItem | undefined>(() => {
	return getPlanCartItems(props.plan.id)?.find(
		(item) =>
			item.lineItem?.category === PbProductType.Package &&
			item.lineItem?.subcategory !== PbSubcategory.PackageEssentials
	)
})

const serviceCartItems = computed<ReviewSlideCartEntry[]>(() =>
	getReviewSlideCartItemsBySection(
		getPlanCartItems(props.plan.id) ?? [],
		'funeralService'
	)
)

const serviceCustomCartItems = computed<CustomCartItem[]>(() =>
	getReviewSlideCustomCartItemsBySection(
		props.plan.customCartItems,
		'funeralService'
	)
)

const merchandiseCartItems = computed<ReviewSlideCartEntry[]>(() =>
	getReviewSlideCartItemsBySection(
		getPlanCartItems(props.plan.id) ?? [],
		'funeralMerchandise'
	)
)

const merchandiseCustomCartItems = computed<CustomCartItem[]>(() =>
	getReviewSlideCustomCartItemsBySection(
		props.plan.customCartItems,
		'funeralMerchandise'
	)
)

const cashAdvanceCartItems = computed<ReviewSlideCartEntry[]>(() =>
	getReviewSlideCartItemsBySection(
		getPlanCartItems(props.plan.id) ?? [],
		'essentials'
	)
)

const essentialsCustomCartItems = computed<CustomCartItem[]>(() =>
	getReviewSlideCustomCartItemsBySection(
		props.plan.customCartItems,
		'essentials'
	)
)

// Premium Everplans that comes free with an isNoCostEverplans package is not listed as its own item.
// Freemium and separately added premium Everplans still show.
const showEverplansCartLine = computed(() => {
	if (!authStore.isEverplansAuthorized) {
		return false
	}

	const hasFreeEverplans =
		cartStore.cart[props.plan.id]?.includesFreeEverplansPremium ?? false

	return !hasFreeEverplans
})

// Check if a plan has any non-essentials packages soft-deleted
const oapParentDeleted = (plan: Plan) => {
	const nonEssPkgs =
		getPlanCartItems(plan.id)?.filter(
			(pkg) =>
				pkg.lineItem?.category === PbProductType.Package &&
				pkg.lineItem?.subcategory !== PbSubcategory.PackageEssentials
		) ?? []
	return nonEssPkgs.some((pkg) => pkg.isSoftDeleted)
}

const getCartEntryForDisplay = (
	entry: ReviewSlideCartEntry
): CartItemAndLineItem | CartSubItemAndLineItem =>
	isCartSubItem(entry) ? { ...entry, subItems: [], quantity: 1 } : entry

// If removed, OAP should be removed from the plan and the mergedDocument should be invalidated.
const handleOapChange = async (isIncluded: boolean, plan: Plan) => {
	let completedSteps = [...plan.completedSteps]

	if (!isIncluded) {
		completedSteps = plan.completedSteps.filter(
			(step) => step !== DocumentType.OAPEnrollment
		)
		await documentStore.removePlanHolderMergedDocument(plan.id)
	}

	await planStore.updatePlan(plan.id, {
		completedSteps: completedSteps,
		oapIncluded: isIncluded,
		oapStep: 'html',
	})
}

// If removed, Everplans should be removed from the plan and the mergedDocument should be invalidated.
const handleEverplansChange = async (isIncluded: boolean, plan: Plan) => {
	let completedSteps = [...plan.completedSteps]

	if (!isIncluded) {
		completedSteps = plan.completedSteps.filter(
			(step) => step !== DocumentType.EverplansEnrollment
		)
		await documentStore.removePlanHolderMergedDocument(plan.id)
	}

	// if Everplans has been removed from the cart, their previous errors should be removed
	const newErrors = plan.errors.filter(
		(e) => e.type !== PlanErrorType.EverplansEmail
	)

	await planStore.updatePlan(plan.id, {
		errors: newErrors,
		completedSteps: completedSteps,
		everplansIncluded: isIncluded,
		everplansStep: 'html',
	})
}

const onAdvanceButtonClick = () => {
	const controller = usePresentationController()
	const inflationSlide = controller.allSlides?.find(
		(slide) => slide.component === 'inflation-examples'
	)
	if (!inflationSlide) {
		throw new Error('Could not find inflation slide')
	}
	controller.gotoSlideId(inflationSlide._uid)
}
</script>
